import React from 'react';
import { useTranslation } from 'react-i18next';
import { TextInput, Select, Switch } from '@origo/origo-ui';
import PlanDetailHeader from '../../components/PlanDetailHeader';
import PageSection from '../../components/PageSection';
import FormFooterActions from '../plan-info/components/FormFooterActions';
import type { NewPlanFormValues, SelectOption } from '../../types';
import styles from './NewPlanPage.module.css';

export interface NewPlanOptions {
    plantype: SelectOption[];
    planstatus: SelectOption[];
    lovreferanse: SelectOption[];
    forslagsstillerType: SelectOption[];
}

interface NewPlanPageProps {
    values: NewPlanFormValues;
    fieldErrors: Partial<Record<keyof NewPlanFormValues, string>>;
    options: NewPlanOptions;
    optionsLoading?: boolean;
    onChange: <K extends keyof NewPlanFormValues>(field: K, value: NewPlanFormValues[K]) => void;
    onCancel: () => void;
    onSave: () => void;
    isSaving?: boolean;
}

const NewPlanPage: React.FC<NewPlanPageProps> = ({
    values,
    fieldErrors,
    options,
    optionsLoading,
    onChange,
    onCancel,
    onSave,
    isSaving,
}) => {
    const { t } = useTranslation();

    return (
        <article className={styles.page}>
            <PlanDetailHeader title={t('newPlan.title')} subtitle={t('newPlan.subtitle')} />

            <PageSection title={t('planInfo.sections.nameStatus')} asFieldset>
                <TextInput
                    label={t('planInfo.fields.plannavn')}
                    placeholder={t('planInfo.fields.plannavnPlaceholder')}
                    value={values.plannavn}
                    error={fieldErrors.plannavn}
                    onChange={e => onChange('plannavn', e.target.value)}
                />
                <div className={styles.twoColumn}>
                    <Select
                        label={t('planInfo.fields.plantype')}
                        placeholder={t('planInfo.fields.plantypePlaceholder')}
                        value={values.plantype}
                        error={fieldErrors.plantype}
                        disabled={optionsLoading}
                        options={options.plantype}
                        onChange={e => onChange('plantype', e.target.value)}
                    />
                    <Select
                        label={t('planInfo.fields.planstatus')}
                        placeholder={t('planInfo.fields.planstatusPlaceholder')}
                        value={values.planstatus}
                        error={fieldErrors.planstatus}
                        disabled={optionsLoading}
                        options={options.planstatus}
                        onChange={e => onChange('planstatus', e.target.value)}
                    />
                </div>
                <Switch
                    label={t('newPlan.fields.planbestemmelser')}
                    checked={values.planbestemmelser}
                    onChange={v => onChange('planbestemmelser', v)}
                />
            </PageSection>

            <PageSection title={t('planInfo.sections.lawRef')} asFieldset>
                <Select
                    label={t('planInfo.fields.lovreferanse')}
                    placeholder={t('planInfo.fields.lovreferansePlaceholder')}
                    value={values.lovreferanse}
                    error={fieldErrors.lovreferanse}
                    disabled={optionsLoading}
                    options={options.lovreferanse}
                    onChange={e => onChange('lovreferanse', e.target.value)}
                />
            </PageSection>

            <PageSection title={t('newPlan.sections.identification')} asFieldset>
                <div className={styles.twoColumn}>
                    <TextInput
                        label={t('newPlan.fields.admomrkode')}
                        placeholder={t('newPlan.fields.admomrkodePlaceholder')}
                        value={values.admomrkode}
                        error={fieldErrors.admomrkode}
                        onChange={e => onChange('admomrkode', e.target.value)}
                    />
                    <TextInput
                        label={t('newPlan.fields.planId')}
                        placeholder={t('newPlan.fields.planIdPlaceholder')}
                        value={values.planId}
                        error={fieldErrors.planId}
                        onChange={e => onChange('planId', e.target.value)}
                    />
                </div>
                <div className={styles.twoColumn}>
                    <TextInput
                        label={t('newPlan.fields.saksnummer')}
                        placeholder={t('newPlan.fields.saksnummerPlaceholder')}
                        value={values.saksnummer}
                        error={fieldErrors.saksnummer}
                        onChange={e => onChange('saksnummer', e.target.value)}
                    />
                    <Select
                        label={t('newPlan.fields.forslagsstillerType')}
                        placeholder={t('newPlan.fields.forslagsstillerTypePlaceholder')}
                        value={values.forslagsstillerType}
                        error={fieldErrors.forslagsstillerType}
                        disabled={optionsLoading}
                        options={options.forslagsstillerType}
                        onChange={e => onChange('forslagsstillerType', e.target.value)}
                    />
                </div>
            </PageSection>

            <FormFooterActions onCancel={onCancel} onSave={onSave} isSaving={isSaving} />
        </article>
    );
};

export default NewPlanPage;
